import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  message,
  Modal,
  Dropdown,
  Menu,
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
  Select,
  Pagination,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  addEquipment,
  editEquipment,
  removeEquipment,
  fetchEquipmentsByGeneration,
} from '../../../store/actions/vehicleEquipments-actions';

const { Option } = Select;

const VehicleEquipments = ({ equipments, generations }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [selectedGenerationList, setSelectedGenerationList] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const { filteredEquipments } = useSelector((state) => state.equipments);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;

  const handleSubmitEquipment = () => {
    form
      .validateFields()
      .then((values) => {
        const { equipmentName } = values;

        if (editingEquipment) {
          dispatch(
            editEquipment({
              equipmentId: editingEquipment.id,
              body: { name: equipmentName },
            })
          );
        } else {
          dispatch(
            addEquipment({
              name: equipmentName,
              vehicleGenerationId: selectedGeneration,
            })
          );
        }
        form.resetFields();
        setOpenModal(false);
        setEditingEquipment(null);
        setSelectedGeneration(null);
      })
      .catch((info) => {
        message.error('Equipment name and generation are required!');
      });
  };

  const handleEditEquipment = (equipment) => {
    setEditingEquipment(equipment);
    form.setFieldsValue({
      equipmentName: equipment.name,
      vehicleGeneration: equipment.vehicleGenerationId,
    });

    setSelectedGeneration(equipment.vehicleGenerationId);
    setOpenModal(true);
  };

  const handleDeleteEquipment = (equipmentId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this equipment?',
      onOk: () => dispatch(removeEquipment(equipmentId)),
    });
  };

  const menuItems = (equipment) => [
    {
      label: t('common.edit'),
      key: 'edit',
      icon: <EditOutlined />,
      onClick: () => handleEditEquipment(equipment),
    },
    {
      label: t('common.delete'),
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      onClick: () => handleDeleteEquipment(equipment.id),
    },
  ];

  useEffect(() => {
    if (selectedGenerationList) {
      dispatch(
        fetchEquipmentsByGeneration({
          vehicleGenerationId: selectedGenerationList,
        })
      );
    }
  }, [selectedGenerationList, dispatch]);

  const displayedEquipments = selectedGenerationList
    ? filteredEquipments
    : equipments;

  const filteredDisplayedEquipments = displayedEquipments.filter((equipment) =>
    equipment.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginateEquipments = filteredDisplayedEquipments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  let content;

  content = (
    <div className="container-fluid p-0">
      <Card
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <span>{t('vehicles.equipments')}</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <Select
                placeholder="Select Generation"
                value={selectedGenerationList}
                onChange={setSelectedGenerationList}
                style={{ width: 200, marginRight: 16 }}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {generations.map((make) => (
                  <Option key={make.id} value={make.id}>
                    {make.name}
                  </Option>
                ))}
              </Select>
              <Input
                placeholder={t('common.searchByName')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 200, marginRight: 16 }}
              />
            </div>
            <Button
              type="primary"
              onClick={() => {
                setOpenModal(true);
                form.resetFields();
                setSelectedGeneration(null);
              }}
            >
              {t('common.add')} {t('vehicles.equipment')}
            </Button>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {paginateEquipments.map((equipment) => (
            <Col
              key={equipment.id}
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={6}
              xxl={4}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Card style={{ flex: '1 0 auto' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      height: '100%',
                    }}
                  >
                    <div>
                      <h3>{equipment.name}</h3>
                    </div>
                    <Dropdown.Button
                      style={{ zIndex: 1 }}
                      overlay={<Menu items={menuItems(equipment)} />}
                    />
                  </div>
                </Card>
              </div>
            </Col>
          ))}
        </Row>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredDisplayedEquipments.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          showQuickJumper
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );

  return (
    <>
      {content}
      <Modal
        title={
          editingEquipment
            ? t('common.edit') + ' ' + t('vehicles.equipment')
            : t('common.add') + ' ' + t('vehicles.equipment')
        }
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          setEditingEquipment(null);
          setSelectedGeneration(null);
        }}
        onOk={handleSubmitEquipment}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="equipmentName"
            label={t('common.name')}
            rules={[
              {
                required: true,
                message: t('form.validationMessages.requiredField', {
                  field: t('common.name'),
                }),
              },
            ]}
          >
            <Input placeholder="Enter equipment name" />
          </Form.Item>
          <Form.Item
            name="vehicleGeneration"
            label="Select Generation"
            rules={[
              {
                required: true,
                message: t('form.validationMessages.requiredField', {
                  field: t('vehicles.generation'),
                }),
              },
            ]}
          >
            <Select
              placeholder="Select a generation"
              value={selectedGeneration}
              onChange={setSelectedGeneration}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={editingEquipment}
            >
              {generations.map((generation) => (
                <Option key={generation.id} value={generation.id}>
                  {generation.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default VehicleEquipments;
